/**
 * The OPTIMA side panel (bata-platform epic 719, phase F1, ADR 9080).
 *
 * A panel docked to the inline end of the window, on every screen, over the page: the page stays
 * readable and scrollable behind it (it is not modal), because the person asks about what they
 * are looking at. It is a landmark of its own (`<aside>`), not a dialog: it is meant to stay open
 * while they work, and nothing outside it is made inert.
 *
 * What it draws, top to bottom: the name with the avatar (the shoe that came out of the box, ADR
 * 9077) and, while OPTIMA works, a line saying what it is doing; the conversation (or, before the
 * first question, the big avatar, what OPTIMA is for and three questions to start from); the
 * question box, and the standing disclaimer. Under each answer:
 *
 *   · the answer, as plain text only (`AnswerText.tsx`, the safe-rendering rule);
 *   · the knowledge-base entries it rests on, as LABELS — title and kind, not links — and the
 *     line «Source: knowledge base»;
 *   · when the assistant sent the person to the report, or suggests it, «Report a problem»,
 *     which opens the existing report form already filled (ADR 9063) for the person to edit and
 *     send;
 *   · with the request intake (bata-platform ADR 2137, ADR 9071), under the newest answer that
 *     carries one, the report OPTIMA is preparing (`DraftCard.tsx`): drawn from its structured
 *     fields, with what is still missing and, once the platform confirms it complete, «Sì, invia la
 *     segnalazione» and «Apri nel modulo». The panel files only on that press; it changes nothing
 *     else.
 *
 * «Nuova conversazione» in the header ends the conversation: what OPTIMA had gathered is forgotten
 * here, and the next question starts afresh.
 *
 * An answer whose numbers the platform could not verify (`gate.numbers: 'blocked'`) is not drawn:
 * a sentence saying so stands in its place. A number the gate corrected is said to be corrected.
 */

import { Loader2, Plus, RotateCcw, Send, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProblemMessage } from '../../api';
import { Button, FOCUS_RING, IconButton, MOTION, Tooltip } from '../../primitives';
import { useFeedback } from '../feedback/FeedbackProvider';

import { ASSISTANT_LIMITS, type AssistantTurn } from './api';
import { AnswerText } from './AnswerText';
import { DraftCard } from './DraftCard';
import {
  FAILURE_KEY, OPTIMA_ANNOUNCE_KEYS, OPTIMA_EXAMPLE_KEYS, OPTIMA_STATUS_LINE_KEYS, answerEndState, avatarStateOf, awaitsConfirmation,
  classifyFailure, currentDraftExchange, draftOf, isConversationClosed, isDecline, isWithheld, offersHandoff,
  type Exchange, type Filing, type OptimaState,
} from './optima';
import { OptimaAvatar } from './OptimaAvatar';

export interface OptimaPanelProps {
  readonly exchanges: readonly Exchange[];
  /** A question is being answered: the box waits, the person's text stays in it. */
  readonly busy: boolean;
  readonly onAsk: (question: string) => void;
  readonly onRetry: (id: number, question: string) => void;
  /** `returnFocus`: hand the focus back to the entry that opened the panel. */
  readonly onClose: (returnFocus: boolean) => void;
  /** The platform offers the request intake: OPTIMA may prepare a report. */
  readonly intake?: boolean;
  /** What became of each confirmed draft, by exchange. */
  readonly filings?: Readonly<Record<number, Filing>>;
  readonly onNewConversation?: () => void;
  readonly onFile?: (exchangeId: number) => void;
  readonly onOpenInForm?: (exchangeId: number) => void;
  readonly onOpenMyReports?: () => void;
  /**
   * What the avatar is doing (`avatarStateOf`, from the conversation's real lifecycle). Without it
   * the panel works it out from what it holds, which is everything but the 600 ms after a filing.
   */
  readonly avatarState?: OptimaState;
}

/** What an exchange needs to draw its draft, when it is the current one. */
interface DraftHandlers {
  readonly filings: Readonly<Record<number, Filing>>;
  readonly currentDraft: number | null;
  readonly onFile: (exchangeId: number) => void;
  readonly onOpenInForm: ((exchangeId: number) => void) | undefined;
  readonly onOpenMyReports: (() => void) | undefined;
}

const NO_FILINGS: Readonly<Record<number, Filing>> = {};
const NOTHING = (): void => undefined;

/** The look of the app's other free-text areas (`FeedbackForm`), in the body face. */
const TEXTAREA = [
  'border-hairline bg-surface-raised text-ink-primary text-body font-ui w-full resize-none rounded-control border px-3 py-2',
  FOCUS_RING,
].join(' ');

/** Within this many characters of the limit the box says how many are left. */
const COUNTER_FROM = 200;

export function OptimaPanel({
  exchanges, busy, onAsk, onRetry, onClose, intake = false, filings = NO_FILINGS, onNewConversation, onFile = NOTHING, onOpenInForm,
  onOpenMyReports, avatarState,
}: OptimaPanelProps): React.JSX.Element {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  // The shoe comes out of the box each time the panel opens: the avatar is drawn in the box for the
  // first frame and takes its state at once, so the exit plays (and, under reduced motion, does not).
  const [out, setOut] = useState(false);
  useEffect(() => { setOut(true); }, []);
  const input = useRef<HTMLTextAreaElement>(null);
  const log = useRef<HTMLDivElement>(null);

  // The cursor is in the box when the panel opens: the person pressed a key or a button to ask.
  useEffect(() => { input.current?.focus(); }, []);

  // Newest at the bottom, and the log follows it.
  useEffect(() => {
    const element = log.current;
    if (element !== null) element.scrollTop = element.scrollHeight;
  }, [exchanges]);

  const send = (): void => {
    const question = draft.trim();
    if (question === '' || busy) return;
    onAsk(question);
    setDraft('');
    // Asking with the button leaves the focus on a button that is about to be disabled; the person
    // will want to read the answer and type the next question, so the cursor goes back to the box.
    input.current?.focus();
  };

  const state = avatarState ?? avatarStateOf({ open: true, exchanges, filings, returned: false });
  const statusKey = OPTIMA_STATUS_LINE_KEYS[state];
  const announceKey = OPTIMA_ANNOUNCE_KEYS[state];

  /** An example question fills the box and leaves the person to read, edit and ask. */
  const fillWith = (question: string): void => {
    setDraft(question);
    input.current?.focus();
  };

  const remaining = ASSISTANT_LIMITS.question.max - draft.length;
  const drafts: DraftHandlers = { filings, currentDraft: currentDraftExchange(exchanges), onFile, onOpenInForm, onOpenMyReports };

  return (
    <aside
      aria-label={t('optima.panel.label')}
      data-optima-panel=""
      onKeyDown={(event) => { if (event.key === 'Escape') { event.stopPropagation(); onClose(true); } }}
      className={`bg-surface-raised border-hairline z-modal shadow-overlay fixed inset-y-0 end-0 flex w-[min(28rem,90vw)] flex-col border-s print:hidden ${MOTION.slideInEnd}`}
    >
      <header className="border-hairline flex items-center justify-between gap-2 border-b p-3">
        <div className="flex min-w-0 items-center gap-2">
          <OptimaAvatar state={out ? state : 'pronto'} size={28} />
          <div className="flex min-w-0 flex-col">
            <h2 className="text-section text-ink-primary font-display uppercase">{t('optima.name')}</h2>
            {/* One line, with dots when it does not fit: «Sto leggendo i dati…» (LEGGIMI «Dove va»). */}
            {statusKey !== undefined && (
              <p data-optima-status="" className="text-micro text-ink-secondary truncate whitespace-nowrap">{t(statusKey)}</p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {onNewConversation !== undefined && exchanges.length > 0 && (
            statusKey === undefined ? (
              <Button variant="ghost" size="sm" icon={<RotateCcw size={12} aria-hidden />} disabled={busy} onClick={onNewConversation}>
                {t('optima.conversation.new')}
              </Button>
            ) : (
              // While the status line takes the room, the button is the «+» alone, so the header fits in 392 px.
              <Tooltip contentKey="optima.conversation.new">
                <IconButton
                  variant="ghost" size="md" icon={<Plus size={16} aria-hidden className="shrink-0" />} disabled={busy}
                  aria-label={t('optima.conversation.new')} onClick={onNewConversation}
                />
              </Tooltip>
            )
          )}
          <IconButton variant="ghost" size="sm" icon={<X size={14} aria-hidden className="shrink-0" />} aria-label={t('optima.panel.close')} onClick={() => onClose(true)} />
        </div>
      </header>

      {/* The state, said to those who cannot see the shoe (ADR 9077): polite, so it never cuts across what they are reading. */}
      <div aria-live="polite" aria-atomic="true" data-optima-live="" className="sr-only">
        {announceKey === null ? '' : t(announceKey)}
      </div>

      <div ref={log} role="log" aria-label={t('optima.log.label')} className="flex grow flex-col gap-4 overflow-auto p-3">
        {exchanges.length === 0 && (
          <div className="flex flex-col items-start gap-3" data-optima-empty="">
            {/* Still: the one animated shoe on the screen is the header's. */}
            <OptimaAvatar state={out ? 'ascolto' : 'pronto'} size={96} staticState />
            <div className="flex flex-col gap-1">
              <p className="text-body text-ink-primary font-semibold">{t('optima.intro.title')}</p>
              <p className="text-body text-ink-secondary">{t(intake ? 'optima.intake.intro' : 'optima.intro.body')}</p>
            </div>
            <ul aria-label={t('optima.example.label')} className="flex w-full flex-col gap-2">
              {OPTIMA_EXAMPLE_KEYS.map((key) => (
                <li key={key}>
                  <button
                    type="button"
                    data-optima-example=""
                    onClick={() => fillWith(t(key))}
                    className={`border-hairline bg-surface-raised text-body text-ink-primary hover:bg-surface-sunken rounded-control flex min-h-[44px] w-full items-center border px-3 py-2 text-start ${FOCUS_RING}`}
                  >
                    {t(key)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {exchanges.map((exchange) => (
          <ExchangeView key={exchange.id} exchange={exchange} onRetry={onRetry} onClose={onClose} drafts={drafts} />
        ))}
      </div>

      <form
        onSubmit={(event) => { event.preventDefault(); send(); }}
        noValidate
        className="border-hairline flex flex-col gap-2 border-t p-3"
      >
        <label htmlFor="optima-question" className="sr-only">{t('optima.input.label')}</label>
        <textarea
          id="optima-question"
          ref={input}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            // Enter asks, Shift+Enter breaks the line; a key that confirms an IME composition is not Enter.
            if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
            event.preventDefault();
            send();
          }}
          maxLength={ASSISTANT_LIMITS.question.max}
          rows={3}
          placeholder={t('optima.input.placeholder')}
          className={TEXTAREA}
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-caption text-ink-secondary">
            {remaining <= COUNTER_FROM ? t('optima.input.remaining', { count: remaining }) : ''}
          </span>
          <Button type="submit" variant="primary" size="md" icon={<Send size={14} aria-hidden />} disabled={busy || draft.trim() === ''}>
            {t('optima.ask')}
          </Button>
        </div>
      </form>

      <p className="text-caption text-ink-secondary border-hairline border-t p-3">{t(intake ? 'optima.intake.footer' : 'optima.footer.disclaimer')}</p>
    </aside>
  );
}

interface ExchangeViewProps {
  readonly exchange: Exchange;
  readonly onRetry: (id: number, question: string) => void;
  readonly onClose: (returnFocus: boolean) => void;
  readonly drafts: DraftHandlers;
}

function ExchangeView({ exchange, onRetry, onClose, drafts }: ExchangeViewProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2" data-optima-exchange="">
      <div className="bg-pill rounded-control flex flex-col gap-1 px-3 py-2">
        {/* Primary ink: secondary measures 4.31:1 on the pill, under WCAG AA (as for the scope chips in the bar). */}
        <span className="text-micro text-ink-primary uppercase">{t('optima.you')}</span>
        {/* The person's own words, drawn as text like the answer: a pasted fragment is not run either. */}
        <p className="text-body text-ink-primary whitespace-pre-wrap break-words">{exchange.question}</p>
      </div>
      {exchange.state === 'pending' && (
        <p role="status" aria-busy="true" className="text-body text-ink-secondary flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" aria-hidden />
          {t('optima.loading')}
        </p>
      )}
      {exchange.state === 'failed' && <FailureView exchange={exchange} onRetry={onRetry} />}
      {exchange.state === 'answered' && exchange.turn !== undefined && (
        <TurnView
          turn={exchange.turn} question={exchange.question} onClose={onClose} hasDraft={draftOf(exchange.turn) !== null}
          endState={answerEndState(exchange.turn, awaitsConfirmation(exchange, drafts.currentDraft, drafts.filings[exchange.id]))}
        />
      )}
      {exchange.state === 'answered' && drafts.currentDraft === exchange.id && <ExchangeDraft exchange={exchange} drafts={drafts} />}
    </div>
  );
}

function FailureView({ exchange, onRetry }: { readonly exchange: Exchange; readonly onRetry: (id: number, question: string) => void }): React.JSX.Element {
  const { t } = useTranslation();
  const problemText = useProblemMessage();
  // A conversation that is over is not an assistant that is off: the question can be asked again.
  const closed = isConversationClosed(exchange.error);
  const failure = closed ? 'other' : classifyFailure(exchange.error);
  return (
    <div role="alert" className="flex flex-col gap-1">
      <p className="text-body text-status-critical font-semibold">{t('optima.error.title')}</p>
      <p className="text-caption text-ink-secondary">
        {closed
          ? t('optima.conversation.closed')
          : failure === 'other' ? problemText(exchange.error) : t(FAILURE_KEY[failure])}
      </p>
      {/* A disabled assistant stays disabled; the others may come back. */}
      {failure !== 'disabled' && (
        <div>
          <Button variant="secondary" size="sm" onClick={() => onRetry(exchange.id, exchange.question)}>{t('optima.retry')}</Button>
        </div>
      )}
    </div>
  );
}

function ExchangeDraft({ exchange, drafts }: { readonly exchange: Exchange; readonly drafts: DraftHandlers }): React.JSX.Element | null {
  const current = draftOf(exchange.turn);
  if (current === null) return null;
  const { onOpenInForm, onOpenMyReports } = drafts;
  return (
    <DraftCard
      intake={current}
      filing={drafts.filings[exchange.id]}
      onFile={() => drafts.onFile(exchange.id)}
      {...(onOpenInForm === undefined ? {} : { onOpenInForm: () => onOpenInForm(exchange.id) })}
      {...(onOpenMyReports === undefined ? {} : { onOpenMyReports })}
    />
  );
}

interface TurnViewProps {
  readonly turn: AssistantTurn;
  readonly question: string;
  readonly onClose: (returnFocus: boolean) => void;
  /** The answer carries a draft: the card's actions replace the plain «Report a problem». */
  readonly hasDraft: boolean;
  /** How the answer ended, for the avatar beside it: `pronto`, `nonso` or `bozza`. */
  readonly endState: Extract<OptimaState, 'pronto' | 'nonso' | 'bozza'>;
}

function TurnView({ turn, question, onClose, hasDraft, endState }: TurnViewProps): React.JSX.Element {
  const { t } = useTranslation();
  const feedback = useFeedback();
  const withheld = isWithheld(turn);

  /** The report, open on the screen the person is on, with the question and the assistant's own summary to edit. */
  const reportIt = (): void => {
    if (feedback === null) return;
    const summary = turn.handoff.summary?.trim() ?? '';
    feedback.open({
      description: summary === ''
        ? t('optima.handoff.description', { question })
        : t('optima.handoff.descriptionWithSummary', { question, summary }),
    });
    // The report's form takes the focus; the panel's conversation is kept for when it is opened again.
    onClose(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="flex items-center gap-2">
        <OptimaAvatar state={endState} size={20} staticState />
        <span className="text-micro text-ink-secondary uppercase">{t('optima.name')}</span>
      </span>
      {withheld ? (
        <p className="text-body text-ink-primary">{t('optima.notice.withheld')}</p>
      ) : (
        <AnswerText text={turn.answer} />
      )}
      {!withheld && turn.gate.numbers === 'corrected' && <p className="text-caption text-ink-secondary">{t('optima.notice.corrected')}</p>}
      {!withheld && isDecline(turn.action) && (
        <p className="text-caption text-ink-secondary">{t(turn.action === 'refuse' ? 'optima.notice.refused' : 'optima.notice.notDocumented')}</p>
      )}
      {!withheld && (
        <div className="flex flex-col gap-1">
          {turn.cited.length > 0 && (
            <ul aria-label={t('optima.sources.label')} className="flex flex-wrap gap-1">
              {turn.cited.map((entry) => (
                // A label, not a link and not a button: it says what the answer rests on and goes nowhere.
                // The title only, in the person's language: the entry's kind is the knowledge base's
                // internal word and OPTIMA speaks the planner's (owner's ruling, 2026-10-16).
                <li key={entry.id} className="bg-pill text-caption text-ink-primary rounded-chip inline-flex max-w-full items-baseline gap-1 px-2 py-1">
                  <span className="truncate" title={entry.title}>{entry.title}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-caption text-ink-secondary">{t('optima.source.note')}</p>
        </div>
      )}
      {offersHandoff(turn) && !hasDraft && feedback !== null && (
        <div className="flex flex-col gap-1">
          <p className="text-caption text-ink-secondary">{t('optima.handoff.hint')}</p>
          <div>
            <Button variant="secondary" size="sm" onClick={reportIt}>{t('feedback.report.open')}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
